import { useEffect, useRef, useState } from 'react'

import { askYogiLoadingMessages } from 'features/ask-yogi/constants'

/** Seconds for one shimmer flutter (one visible sweep across a phrase). */
export const ANIMATION_DURATION = 1.5
/** Number of flutters a phrase shows before cycling to the next. */
export const ANIMATION_CYCLES_PER_WORD = 3
/** Cross-fade length, each direction, when swapping phrases. */
export const PHRASE_FADE_MS = 400
/** How long one phrase holds before rotating — the shared working cadence. */
export const DEFAULT_PHRASE_DURATION_MS =
  ANIMATION_DURATION * ANIMATION_CYCLES_PER_WORD * 1000

export interface CyclingLoadingPhrase {
  phrase: string
  /** False during the cross-fade window while the phrase swaps. */
  visible: boolean
}

/**
 * Rotates through the shared Ask Yogi loading phrases while active: hold one
 * phrase for `phraseDurationMs`, fade out, swap, fade back in. One mechanism
 * for every working indicator — the conversation loader and the live
 * run-steps header — so they cycle the same phrases at the same cadence.
 * Inactive, it resets so the next activation starts from the first phrase.
 */
export const useCyclingLoadingPhrase = (
  isActive: boolean,
  phraseDurationMs: number = DEFAULT_PHRASE_DURATION_MS,
): CyclingLoadingPhrase => {
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isActive) return

    setIndex(0)
    setVisible(true)

    const cycle = setInterval(() => {
      // Fade the current phrase out, swap it, then fade the next one in.
      setVisible(false)
      fadeTimeoutRef.current = setTimeout(() => {
        setIndex((prev) => (prev + 1) % askYogiLoadingMessages.length)
        setVisible(true)
      }, PHRASE_FADE_MS)
    }, phraseDurationMs)

    return () => {
      clearInterval(cycle)
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current)
    }
  }, [isActive, phraseDurationMs])

  return { phrase: askYogiLoadingMessages[index]!, visible }
}
